import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={movie.coverImage}
            alt={movie.title}
            title={`${movie.title} - 日韩大片在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-between py-0.5 min-w-0">
          <div>
            <h4 className="font-semibold text-sm text-foreground-900 line-clamp-1">{movie.title}</h4>
            <p className="text-xs text-foreground-500 mt-1">{movie.year} · {movie.director}</p>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
            </span>
            <span className="text-sm font-semibold text-accent-500">{movie.rating}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group relative block rounded-xl overflow-hidden cursor-pointer h-full"
      >
        <div className="w-full h-80">
          <img
            src={movie.coverImage}
            alt={movie.title}
            title={`${movie.title} - 日韩大片在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-primary-500/90 text-white text-xs rounded-full whitespace-nowrap">
              {movie.year}
            </span>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-accent-500 text-sm"></i>
            </span>
            <span className="text-white font-semibold">{movie.rating}</span>
          </div>
          <h3 className="text-xl font-heading font-bold text-white mb-1">{movie.title}</h3>
          <p className="text-sm text-white/70 line-clamp-2">{movie.description}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all cursor-pointer"
    >
      <div className="w-full aspect-[2/3] overflow-hidden">
        <img
          src={movie.coverImage}
          alt={movie.title}
          title={`${movie.title} - 日韩大片在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
          </span>
          <span className="text-sm font-semibold text-accent-500">{movie.rating}</span>
          <span className="text-xs text-foreground-400">{movie.year}</span>
        </div>
        <h4 className="font-heading font-semibold text-foreground-900 mb-1 line-clamp-1">{movie.title}</h4>
        <p className="text-xs text-foreground-500 line-clamp-2">{movie.description}</p>
        <div className="flex flex-wrap gap-1 mt-3">
          {movie.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}